import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Header } from "./components/Header";
import { HeroSection } from "./components/HeroSection";
import { TransformationSection } from "./components/TransformationSection";
import { ServicesSection } from "./components/ServicesSection";
import { PricingSection } from "./components/PricingSection";
import { HowItWorksSection } from "./components/HowItWorksSection";
import { BlogsSection } from "./components/BlogsSection";
import { BlogsPage } from "./components/BlogsPage";
import { BlogDetail } from "./components/BlogDetail";
import { AppointmentSection } from "./components/AppointmentSection";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/sonner";
import { OfferBanner } from "./components/OfferBanner";

// CRO Components
import { SocialProofBar } from "./components/SocialProof";
import { TestimonialsSection } from "./components/TestimonialsSection";
import { PartnersLogos, TrustIndicators } from "./components/PartnersSection";
import { FAQSection } from "./components/FAQSection";
import { LeadCapturePopup } from "./components/LeadCapturePopup";
import ChatWidget from "./components/ChatWidget";
import FDILandingPage from "./components/FDILandingPage";
import { ExitQuiz, SocialProofNotification, GatedResource } from "./components/LeadMagnets";
import { InsightsShowcase } from "./components/InsightsShowcase";
import { CookieConsent } from "./components/CookieConsent";

// New Virtual CFO Consultancy sections
import { WhoWeHelpSection } from "./components/WhoWeHelpSection";
import { AboutSection, TeamSection, CTABanner } from "./components/AboutTeamSections";
import { EngagementSection } from "./components/EngagementSection";
import { AboutUsPage } from "./components/AboutUsPage";

// Admin Components
import { AdminLogin } from "./components/admin/AdminLogin";
import { AdminDashboard } from "./components/admin/AdminDashboard";
import { BlogEditor } from "./components/admin/BlogEditor";
import { ProtectedRoute } from "./components/admin/ProtectedRoute";
import RetargetingDashboard from "./components/admin/RetargetingDashboard";
import LeadPipeline from "./components/admin/LeadPipeline";
import AIBlogDashboard from "./components/admin/AIBlogDashboard";
import QuoteDashboard from "./components/admin/QuoteDashboard";
import EmailSettings from "./components/admin/EmailSettings";
import BlogAnalytics from "./components/admin/BlogAnalytics";
import ContentEngine from "./components/admin/ContentEngine";
import QuoteView from "./pages/QuoteView";
import ContractDashboard from "./components/admin/ContractDashboard";
import InternalLinksDashboard from "./components/admin/InternalLinksDashboard";
import LeadMagnetDashboard from "./components/admin/LeadMagnetDashboard";
import ABTestingDashboard from "./components/admin/ABTestingDashboard";
import { LinkedInDashboard } from "./components/admin/LinkedInDashboard";
import SubscriberDashboard from "./components/admin/SubscriberDashboard";
import PublicContractPage from "./pages/PublicContractPage";
import { BlogCategoryPage } from "./components/BlogCategoryPage";
import { SubscribeVerified } from "./components/SubscribeVerified";

const Home = () => {
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);
  const [interestedPlan, setInterestedPlan] = useState('');
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const el = document.getElementById(location.state.scrollTo);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [location.state]);

  const handleBookAppointment = (planName) => {
    if (planName && typeof planName === 'string') setInterestedPlan(planName);
    setIsAppointmentOpen(true);
  };

  const handleCloseAppointment = () => {
    setIsAppointmentOpen(false);
  };

  useEffect(() => {
    document.title = 'Virtual CFO Consultancy | Expert CFO Services for SMEs, Startups & Family Businesses';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <meta name="description" content="Virtual CFO Consultancy — expert CFO services for Indian SMEs, startups and family businesses. Financial strategy, MIS reporting, compliance and fundraising support by Chartered Accountants." />
      <meta name="keywords" content="virtual cfo, cfo services, chartered accountant, financial strategy, MIS reporting, cash flow management, GST compliance, fundraising support, startup finance, SME accounting, family business advisory" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${window.location.origin}/`} />
      <meta property="og:title" content="Virtual CFO Consultancy | Expert CFO Services for SMEs & Startups" />
      <meta property="og:description" content="CFO-level financial expertise for growing businesses — without the full-time cost. Book a free consultation with our CA partners." />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={`${window.location.origin}/`} />
      <meta name="twitter:title" content="Virtual CFO Consultancy | Expert CFO Services" />
      <meta name="twitter:description" content="CFO-level financial expertise for growing businesses — without the full-time cost. Flexible retainer models." />
      <meta name="twitter:image" content={`${window.location.origin}/twitter-image.jpg`} />
      <link rel="canonical" href={`${window.location.origin}/`} />
      <OfferBanner onBookAppointment={handleBookAppointment} />
      <Header onBookAppointment={handleBookAppointment} />
      <HeroSection onBookAppointment={handleBookAppointment} />
      <SocialProofBar />
      <WhoWeHelpSection />
      <ServicesSection />
      <InsightsShowcase onBookAppointment={handleBookAppointment} />
      <TrustIndicators />
      <TestimonialsSection />
      <EngagementSection onBookAppointment={handleBookAppointment} />
      
      {/* Gated Resources - Lead Magnets */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Free Resources for Business Owners</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <GatedResource title="GST Compliance Checklist 2026" description="Complete month-by-month GST filing checklist with deadlines and penalty rates." resourceType="gst-checklist" onBookAppointment={handleBookAppointment} />
            <GatedResource title="TDS Rate Card & Calendar" description="All TDS rates at a glance + quarterly due dates. Print-ready PDF." resourceType="tds-calendar" onBookAppointment={handleBookAppointment} />
            <GatedResource title="Startup Finance Playbook" description="Step-by-step guide to setting up your finance function from Day 1." resourceType="startup-playbook" onBookAppointment={handleBookAppointment} />
          </div>
        </div>
      </section>

      <AboutSection />
      <TeamSection onBookAppointment={handleBookAppointment} />
      <PartnersLogos />
      <HowItWorksSection />
      <FAQSection onBookAppointment={handleBookAppointment} />
      <BlogsSection />
      <CTABanner onBookAppointment={handleBookAppointment} />
      <Footer onBookAppointment={handleBookAppointment} />
      <AppointmentSection 
        isOpen={isAppointmentOpen} 
        onClose={handleCloseAppointment}
        interestedPlan={interestedPlan}
      />
      <ExitQuiz onBookAppointment={handleBookAppointment} />
      <SocialProofNotification />
      <ChatWidget onBookAppointment={handleBookAppointment} />
      <CookieConsent />
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/fdi" element={<FDILandingPage />} />
            <Route path="/india-entry" element={<FDILandingPage />} />
            <Route path="/incorporation" element={<FDILandingPage />} />
            <Route path="/blogs" element={<BlogsPage />} />
            <Route path="/blogs/:category" element={<BlogCategoryPage />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/subscribe/verified" element={<SubscribeVerified />} />
            <Route path="/quote/:token" element={<QuoteView />} />
            <Route path="/contract/:token" element={<PublicContractPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/blog/new" 
              element={
                <ProtectedRoute>
                  <BlogEditor />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/blog/edit/:blogId" 
              element={
                <ProtectedRoute>
                  <BlogEditor />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/retargeting" 
              element={
                <ProtectedRoute>
                  <RetargetingDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/leads" 
              element={
                <ProtectedRoute>
                  <LeadPipeline />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/ai-blogs" 
              element={
                <ProtectedRoute>
                  <AIBlogDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/quotes" 
              element={
                <ProtectedRoute>
                  <QuoteDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/email-settings" 
              element={
                <ProtectedRoute>
                  <EmailSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/blog-analytics" 
              element={
                <ProtectedRoute>
                  <BlogAnalytics />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/content-engine" 
              element={
                <ProtectedRoute>
                  <ContentEngine />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/contracts" 
              element={
                <ProtectedRoute>
                  <ContractDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/internal-links" 
              element={
                <ProtectedRoute>
                  <InternalLinksDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/lead-magnets" 
              element={
                <ProtectedRoute>
                  <LeadMagnetDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/ab-testing" 
              element={
                <ProtectedRoute>
                  <ABTestingDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/linkedin" 
              element={
                <ProtectedRoute>
                  <LinkedInDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/subscribers" 
              element={
                <ProtectedRoute>
                  <SubscriberDashboard />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </BrowserRouter>
      </div>
  );
}

export default App;
